import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Avatar,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
  CircularProgress,
  Alert,
  TextField,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Pets as PetsIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  DataUsage as DataIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  PhotoCamera as PhotoCameraIcon,
  Analytics as AnalyticsIcon,
  Star as StarIcon,
  ExpandMore as ExpandMoreIcon,
  LocalFireDepartment as FireIcon,
  Share as ShareIcon,
  Feedback as FeedbackIcon,
  CheckCircle as CheckCircleIcon,
  Timeline as TimelineIcon,
  Assignment as AssignmentIcon,
  Sync as SyncIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { Checkbox, FormControlLabel, InputAdornment } from '@mui/material';
import { collection, getDocs, doc, deleteDoc, query, orderBy, updateDoc, setDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { updateProfile } from 'firebase/auth';
import { db, storage, auth } from '../firebase';
import { PetProfile } from '../types/onboarding';
import { calculateAgeFromBirthday, parseDateFromString } from '../utils/birthdayCalculation';
import { PetEditDialog } from './PetEditDialog';
import { stickerTrackingService, UserStickerStats } from '../services/stickerTrackingService';
import { PetShareService } from '../services/petShareService';
import { IntakeService } from '../services/intakeService';
import AnalyticsDashboard from './AnalyticsDashboard';

interface UserProfileModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  userLanguage: 'en' | 'zh-CN' | 'es' | 'fr';
  userDisplayName?: string;
  userEmail?: string;
  userPhotoURL?: string;
  onUserUpdate?: (updates: { displayName?: string; photoURL?: string }) => void;
  onStartOnboarding?: () => void;
}

type SettingsCategory = 'general' | 'profile' | 'pets' | 'intake' | 'progress' | 'statistics' | 'notifications' | 'security' | 'data' | 'feedback';

// Feedback Form Component
const FeedbackForm: React.FC<{
  userDisplayName: string;
  userEmail: string;
  userLanguage?: 'zh-CN' | 'es' | 'fr' | 'en';
  isMobile: boolean;
  userId?: string;
}> = ({ userDisplayName, userEmail, userLanguage = 'en', isMobile, userId }) => {
  const [formData, setFormData] = useState({
    name: userDisplayName,
    email: userEmail,
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Enhanced language detection with automatic detection from user input
  const detectLanguageFromInput = (text: string): 'zh-CN' | 'es' | 'fr' | 'en' => {
    // Chinese detection
    if (/[\u4e00-\u9fff]/.test(text)) {
      return 'zh-CN';
    }
    // Spanish detection
    if (/[áéíóúñü]/i.test(text)) {
      return 'es';
    }
    // French detection
    if (/[àâäéèêëïîôöùûüÿç]/i.test(text)) {
      return 'fr';
    }
    // Default to English
    return 'en';
  };

  // Auto-detect language from form data, but prioritize userLanguage if message is empty
  const detectedLanguage = formData.message.trim() ? detectLanguageFromInput(formData.message) : userLanguage;
  const isChinese = detectedLanguage === 'zh-CN';
  const isSpanish = detectedLanguage === 'es';
  const isFrench = detectedLanguage === 'fr';

  // Debug logging
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_LOGS === 'true') console.log('🔍 FeedbackForm Language Detection:', {
    userLanguage,
    messageText: formData.message,
    detectedLanguage,
    isChinese,
    isSpanish,
    isFrench
  });

  // Enhanced multi-language text mappings with more comprehensive translations
  const getText = (key: string) => {
    if (isChinese) {
      const chineseTexts: Record<string, string> = {
        feedback: "用户反馈",
        feedbackDescription: "分享您的想法和建议，帮助我们改进PetSnap",
        feedbackNameLabel: "姓名",
        feedbackEmailLabel: "邮箱地址",
        feedbackMessageLabel: "反馈内容",
        feedbackMessagePlaceholder: "请告诉我们：\n• 哪些功能需要改进？\n• 您希望看到什么新功能？\n• 使用过程中遇到的问题\n• 其他建议或意见\n\n您的反馈对我们非常宝贵！",
        feedbackSubmitButton: "提交反馈",
        feedbackSubmitting: "正在提交...",
        feedbackSuccess: "感谢您的反馈！我们会认真阅读您的建议，并持续改进PetSnap。",
        feedbackError: "提交失败，请稍后重试。",
        feedbackNameRequired: "请输入您的姓名",
        feedbackEmailRequired: "请输入邮箱地址",
        feedbackMessageRequired: "请输入反馈内容",
        feedbackEmailInvalid: "请输入有效的邮箱地址",
        feedbackThankYou: "感谢您的宝贵意见！",
        feedbackValue: "您的反馈对我们很有价值",
        feedbackReview: "我们会仔细审查您的建议"
      };
      return chineseTexts[key] || key;
    } else if (isSpanish) {
      const spanishTexts: Record<string, string> = {
        feedback: "Comentarios del Usuario",
        feedbackDescription: "Comparte tus pensamientos y sugerencias para ayudarnos a mejorar PetSnap",
        feedbackNameLabel: "Nombre",
        feedbackEmailLabel: "Correo Electrónico",
        feedbackMessageLabel: "Mensaje",
        feedbackMessagePlaceholder: "Por favor comparte con nosotros:\n• ¿Qué funciones necesitan mejoras?\n• ¿Qué nuevas funciones te gustaría ver?\n• Problemas que hayas encontrado\n• Cualquier otra sugerencia\n\n¡Tu opinión es muy valiosa para nosotros!",
        feedbackSubmitButton: "Enviar Comentarios",
        feedbackSubmitting: "Enviando...",
        feedbackSuccess: "¡Gracias por tus comentarios! Revisaremos cuidadosamente tus sugerencias y continuaremos mejorando PetSnap.",
        feedbackError: "Error al enviar. Por favor, inténtalo de nuevo.",
        feedbackNameRequired: "Por favor, ingresa tu nombre",
        feedbackEmailRequired: "Por favor, ingresa tu correo electrónico",
        feedbackMessageRequired: "Por favor, escribe tu mensaje",
        feedbackEmailInvalid: "Por favor, ingresa un correo electrónico válido",
        feedbackThankYou: "¡Gracias por tu valiosa opinión!",
        feedbackValue: "Tu feedback es muy valioso para nosotros",
        feedbackReview: "Revisaremos cuidadosamente tus sugerencias"
      };
      return spanishTexts[key] || key;
    } else if (isFrench) {
      const frenchTexts: Record<string, string> = {
        feedback: "Commentaires Utilisateur",
        feedbackDescription: "Partagez vos pensées et suggestions pour nous aider à améliorer PetSnap",
        feedbackNameLabel: "Nom",
        feedbackEmailLabel: "Email",
        feedbackMessageLabel: "Message",
        feedbackMessagePlaceholder: "Veuillez partager avec nous :\n• Quelles fonctionnalités doivent être améliorées ?\n• Quelles nouvelles fonctionnalités aimeriez-vous voir ?\n• Problèmes rencontrés\n• Toute autre suggestion\n\nVotre avis nous est très précieux !",
        feedbackSubmitButton: "Envoyer les Commentaires",
        feedbackSubmitting: "Envoi en cours...",
        feedbackSuccess: "Merci pour vos commentaires ! Nous examinerons attentivement vos suggestions et continuerons à améliorer PetSnap.",
        feedbackError: "Échec de l'envoi. Veuillez réessayer.",
        feedbackNameRequired: "Veuillez entrer votre nom",
        feedbackEmailRequired: "Veuillez entrer votre email",
        feedbackMessageRequired: "Veuillez écrire votre message",
        feedbackEmailInvalid: "Veuillez entrer une adresse email valide",
        feedbackThankYou: "Merci pour votre précieux avis !",
        feedbackValue: "Votre feedback nous est très précieux",
        feedbackReview: "Nous examinerons attentivement vos suggestions"
      };
      return frenchTexts[key] || key;
    } else {
      // English
      const englishTexts: Record<string, string> = {
        feedback: "User Feedback",
        feedbackDescription: "Share your thoughts and suggestions to help us improve PetSnap",
        feedbackNameLabel: "Name",
        feedbackEmailLabel: "Email Address",
        feedbackMessageLabel: "Message",
        feedbackMessagePlaceholder: "Please share with us:\n• What features need improvement?\n• What new features would you like to see?\n• Any issues you've encountered\n• Any other suggestions\n\nYour input is very valuable to us!",
        feedbackSubmitButton: "Submit Feedback",
        feedbackSubmitting: "Submitting...",
        feedbackSuccess: "Thank you for your feedback! We'll carefully review your suggestions and continue improving PetSnap.",
        feedbackError: "Failed to submit. Please try again.",
        feedbackNameRequired: "Please enter your name",
        feedbackEmailRequired: "Please enter your email",
        feedbackMessageRequired: "Please write your message",
        feedbackEmailInvalid: "Please enter a valid email address",
        feedbackThankYou: "Thank you for your valuable input!",
        feedbackValue: "Your feedback is very valuable to us",
        feedbackReview: "We'll carefully review your suggestions"
      };
      return englishTexts[key] || key;
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = getText('feedbackNameRequired');
    }

    if (!formData.email.trim()) {
      newErrors.email = getText('feedbackEmailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = getText('feedbackEmailInvalid');
    }

    if (!formData.message.trim()) {
      newErrors.message = getText('feedbackMessageRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      // Import email service
      const { submitFeedback } = await import('../services/emailService');

      // Prepare feedback data
      const feedbackData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        message: formData.message.trim(),
        language: userLanguage,
        timestamp: new Date().toISOString(),
        userId: userId || undefined,
        userAgent: navigator.userAgent,
        ipAddress: undefined // Will be undefined for client-side storage
      };

      // Submit feedback with email notification
      const result = await submitFeedback(feedbackData);

      if (result.success) {
        console.log('✅ Feedback submitted successfully with ID:', result.feedbackId);

        setShowSuccess(true);
        setFormData({
          name: userDisplayName,
          email: userEmail,
          message: ''
        });

        // Hide success message after 5 seconds
        setTimeout(() => {
          setShowSuccess(false);
        }, 5000);
      } else {
        throw new Error(result.error || 'Failed to submit feedback');
      }

    } catch (error) {
      console.error('Error submitting feedback:', error);
      setErrors({ submit: getText('feedbackError') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  if (showSuccess) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <CheckCircleIcon
            sx={{
              fontSize: isMobile ? 48 : 64,
              color: 'success.main',
              mb: 2
            }}
          />
        </Box>
        <Typography
          variant={isMobile ? "h6" : "h5"}
          color="success.main"
          gutterBottom
          sx={{ fontWeight: 600, mb: 2 }}
        >
          {getText('feedbackThankYou')}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            fontSize: isMobile ? '0.9rem' : '1rem',
            mb: 2,
            lineHeight: 1.6
          }}
        >
          {getText('feedbackValue')}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontSize: isMobile ? '0.8rem' : '0.875rem',
            fontStyle: 'italic'
          }}
        >
          {getText('feedbackReview')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant={isMobile ? "h6" : "h5"}
        gutterBottom
        sx={{ fontWeight: 600, mb: 3 }}
      >
        {getText('feedback')}
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        paragraph
        sx={{
          fontSize: isMobile ? '0.9rem' : '1rem',
          lineHeight: 1.6,
          mb: 3
        }}
      >
        {getText('feedbackDescription')}
      </Typography>

      <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 600 }}>
        <TextField
          fullWidth
          label={getText('feedbackNameLabel')}
          value={formData.name}
          onChange={handleChange('name')}
          error={!!errors.name}
          helperText={errors.name}
          margin="normal"
          required
          disabled={isSubmitting}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label={getText('feedbackEmailLabel')}
          type="email"
          value={formData.email}
          onChange={handleChange('email')}
          error={!!errors.email}
          helperText={errors.email}
          margin="normal"
          required
          disabled={isSubmitting}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label={getText('feedbackMessageLabel')}
          multiline
          rows={6}
          value={formData.message}
          onChange={handleChange('message')}
          error={!!errors.message}
          helperText={errors.message}
          placeholder={getText('feedbackMessagePlaceholder')}
          margin="normal"
          required
          disabled={isSubmitting}
          sx={{ mb: 3 }}
        />

        {errors.submit && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.submit}
          </Alert>
        )}

        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : <FeedbackIcon />}
          fullWidth={isMobile}
          sx={{
            minHeight: isMobile ? 48 : 40,
            fontSize: isMobile ? '1rem' : '0.875rem'
          }}
        >
          {isSubmitting ? getText('feedbackSubmitting') : getText('feedbackSubmitButton')}
        </Button>
      </Box>
    </Box>
  );
};

// Embedded Sticker Statistics Component
const EmbeddedStickerStats: React.FC<{ userId: string; userLanguage?: 'zh-CN' | 'es' | 'fr' | 'en' }> = ({ userId, userLanguage = 'en' }) => {
  const [userStats, setUserStats] = useState<UserStickerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedSection, setExpandedSection] = useState<string | false>('overview');

  // Language detection
  const isChinese = userLanguage === 'zh-CN';
  const isSpanish = userLanguage === 'es';
  const isFrench = userLanguage === 'fr';

  // Multi-language text mappings
  const getText = (key: string) => {
    if (isChinese) {
      const chineseTexts: Record<string, string> = {
        totalStickers: '总贴纸数',
        plansCompleted: '完成计划',
        daysCompleted: '完成天数',
        currentStreak: '当前连续',
        longestStreak: '最长连续',
        averageStickersPerPlan: '每计划平均贴纸',
        lastActivity: '最后活动',
        noDataAvailable: '暂无数据',
        loading: '加载中...',
        overview: '概览',
        achievements: '成就',
        recentActivity: '最近活动',
        stickersByPlanType: '按计划类型分类的贴纸',
        dynamic: '动态',
        template: '模板',
        communication: '沟通',
        recentStickerEvents: '最近贴纸事件',
        completedDay: '完成第{day}天',
        completedPlan: '完成计划',
        bonusStickers: '奖励贴纸',
        noRecentActivity: '暂无最近活动',
        viewDetails: '查看详情',
        total: '总计',
        completed: '已完成',
        inProgress: '进行中',
        notStarted: '未开始'
      };
      return chineseTexts[key] || key;
    } else if (isSpanish) {
      const spanishTexts: Record<string, string> = {
        totalStickers: 'Total de Calcomanías',
        plansCompleted: 'Planes Completados',
        daysCompleted: 'Días Completados',
        currentStreak: 'Racha Actual',
        longestStreak: 'Racha Más Larga',
        averageStickersPerPlan: 'Promedio de Calcomanías por Plan',
        lastActivity: 'Última Actividad',
        noDataAvailable: 'No hay datos disponibles',
        loading: 'Cargando...',
        overview: 'Resumen',
        achievements: 'Logros',
        recentActivity: 'Actividad Reciente',
        stickersByPlanType: 'Calcomanías por Tipo de Plan',
        dynamic: 'Dinámico',
        template: 'Plantilla',
        communication: 'Comunicación',
        recentStickerEvents: 'Eventos Recientes de Calcomanías',
        completedDay: 'Día {day} Completado',
        completedPlan: 'Plan Completado',
        bonusStickers: 'Calcomanías de Bonificación',
        noRecentActivity: 'No hay actividad reciente',
        viewDetails: 'Ver Detalles',
        total: 'Total',
        completed: 'Completado',
        inProgress: 'En Progreso',
        notStarted: 'No Iniciado'
      };
      return spanishTexts[key] || key;
    } else if (isFrench) {
      const frenchTexts: Record<string, string> = {
        totalStickers: 'Total d\'Autocollants',
        plansCompleted: 'Plans Terminés',
        daysCompleted: 'Jours Terminés',
        currentStreak: 'Série Actuelle',
        longestStreak: 'Série la Plus Longue',
        averageStickersPerPlan: 'Moyenne d\'Autocollants par Plan',
        lastActivity: 'Dernière Activité',
        noDataAvailable: 'Aucune donnée disponible',
        loading: 'Chargement...',
        overview: 'Aperçu',
        achievements: 'Réalisations',
        recentActivity: 'Activité Récente',
        stickersByPlanType: 'Autocollants par Type de Plan',
        dynamic: 'Dynamique',
        template: 'Modèle',
        communication: 'Communication',
        recentStickerEvents: 'Événements Récents d\'Autocollants',
        completedDay: 'Jour {day} Terminé',
        completedPlan: 'Plan Terminé',
        bonusStickers: 'Autocollants Bonus',
        noRecentActivity: 'Aucune activité récente',
        viewDetails: 'Voir les Détails',
        total: 'Total',
        completed: 'Terminé',
        inProgress: 'En Cours',
        notStarted: 'Non Commencé'
      };
      return frenchTexts[key] || key;
    } else {
      // English
      const englishTexts: Record<string, string> = {
        totalStickers: 'Total Stickers',
        plansCompleted: 'Plans Completed',
        daysCompleted: 'Days Completed',
        currentStreak: 'Current Streak',
        longestStreak: 'Longest Streak',
        averageStickersPerPlan: 'Average Stickers per Plan',
        lastActivity: 'Last Activity',
        noDataAvailable: 'No data available',
        loading: 'Loading...',
        overview: 'Overview',
        achievements: 'Achievements',
        recentActivity: 'Recent Activity',
        stickersByPlanType: 'Stickers by Plan Type',
        dynamic: 'Dynamic',
        template: 'Template',
        communication: 'Communication',
        recentStickerEvents: 'Recent Sticker Events',
        completedDay: 'Completed Day {day}',
        completedPlan: 'Completed Plan',
        bonusStickers: 'Bonus Stickers',
        noRecentActivity: 'No recent activity',
        viewDetails: 'View Details',
        total: 'Total',
        completed: 'Completed',
        inProgress: 'In Progress',
        notStarted: 'Not Started'
      };
      return englishTexts[key] || key;
    }
  };

  // Function to get translated achievement text
  const getTranslatedAchievementText = (achievementId: string, userLanguage: 'zh-CN' | 'es' | 'fr' | 'en' = 'en'): { name: string; description: string } => {
    const achievementTexts: Record<string, Record<string, { name: string; description: string }>> = {
      'first_sticker': {
        'zh-CN': { name: '第一步', description: '获得了您的第一张贴纸！' },
        'es': { name: 'Primeros Pasos', description: '¡Ganaste tu primera calcomanía!' },
        'fr': { name: 'Premiers Pas', description: 'Vous avez gagné votre premier autocollant !' },
        'en': { name: 'First Steps', description: 'Earned your first sticker!' }
      },
      'sticker_collector_10': {
        'zh-CN': { name: '贴纸收集者', description: '获得了10张贴纸！' },
        'es': { name: 'Coleccionista', description: '¡Ganaste 10 calcomanías!' },
        'fr': { name: 'Collectionneur', description: 'Vous avez gagné 10 autocollants !' },
        'en': { name: 'Sticker Collector', description: 'Earned 10 stickers!' }
      },
      'sticker_master_50': {
        'zh-CN': { name: '贴纸大师', description: '获得了50张贴纸！' },
        'es': { name: 'Maestro de Calcomanías', description: '¡Ganaste 50 calcomanías!' },
        'fr': { name: 'Maître des Autocollants', description: 'Vous avez gagné 50 autocollants !' },
        'en': { name: 'Sticker Master', description: 'Earned 50 stickers!' }
      },
      'sticker_legend_100': {
        'zh-CN': { name: '贴纸传奇', description: '获得了100张贴纸！' },
        'es': { name: 'Leyenda de Calcomanías', description: '¡Ganaste 100 calcomanías!' },
        'fr': { name: 'Légende des Autocollants', description: 'Vous avez gagné 100 autocollants !' },
        'en': { name: 'Sticker Legend', description: 'Earned 100 stickers!' }
      },
      'first_plan': {
        'zh-CN': { name: '计划完成者', description: '完成了您的第一个计划！' },
        'es': { name: 'Finalizador de Planes', description: '¡Completaste tu primer plan!' },
        'fr': { name: 'Finisseur de Plans', description: 'Vous avez terminé votre premier plan !' },
        'en': { name: 'Plan Finisher', description: 'Completed your first plan!' }
      },
      'plan_expert_5': {
        'zh-CN': { name: '计划专家', description: '完成了5个计划！' },
        'es': { name: 'Experto en Planes', description: '¡Completaste 5 planes!' },
        'fr': { name: 'Expert en Plans', description: 'Vous avez terminé 5 plans !' },
        'en': { name: 'Plan Expert', description: 'Completed 5 plans!' }
      },
      'streak_master_7': {
        'zh-CN': { name: '连续大师', description: '连续7天完成计划！' },
        'es': { name: 'Maestro de Racha', description: '¡Completaste planes 7 días seguidos!' },
        'fr': { name: 'Maître de Série', description: 'Vous avez terminé des plans 7 jours de suite !' },
        'en': { name: 'Streak Master', description: 'Completed plans for 7 days straight!' }
      },
      'streak_legend_30': {
        'zh-CN': { name: '连续传奇', description: '连续30天完成计划！' },
        'es': { name: 'Leyenda de Racha', description: '¡Completaste planes 30 días seguidos!' },
        'fr': { name: 'Légende de Série', description: 'Vous avez terminé des plans 30 jours de suite !' },
        'en': { name: 'Streak Legend', description: 'Completed plans for 30 days straight!' }
      }
    };

    return achievementTexts[achievementId]?.[userLanguage] || achievementTexts[achievementId]?.['en'] || { name: 'Achievement', description: 'You earned an achievement!' };
  };

  const loadStickerData = useCallback(async () => {
    try {
      setLoading(true);
      console.log('📊 EmbeddedStickerStats: Loading sticker data for user:', userId);
      const userStatsData = await stickerTrackingService.getUserStickerStats(userId);
      console.log('📊 EmbeddedStickerStats: User stats loaded:', userStatsData);
      setUserStats(userStatsData);
    } catch (error) {
      console.error('❌ EmbeddedStickerStats: Error loading sticker data:', error);
      setUserStats({
        totalStickers: 0,
        totalPlansCompleted: 0,
        totalDaysCompleted: 0,
        averageStickersPerPlan: 0,
        currentStreak: 0,
        longestStreak: 0,
        lastActivityDate: new Date(),
        stickersByPlanType: {
          dynamic: 0,
          template: 0,
          communication: 0
        },
        recentStickerEvents: [],
        achievements: []
      });
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      loadStickerData();
    }
  }, [userId, loadStickerData]);

  // Note: Removed auto-refresh interval to prevent flashing
  // Data will be loaded once when component mounts and when userId changes

  const formatDate = (date: Date | string) => {
    // Handle both Date objects and string dates
    const dateObj = date instanceof Date ? date : new Date(date);

    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      return isChinese ? '日期无效' :
        isSpanish ? 'Fecha inválida' :
          isFrench ? 'Date invalide' :
            'Invalid date';
    }

    // Use language-appropriate locale
    const locale = isChinese ? 'zh-CN' :
      isSpanish ? 'es' :
        isFrench ? 'fr' :
          'en-US';

    return new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(dateObj);
  };

  const getStreakColor = (streak: number) => {
    if (streak >= 30) return '#FFD700'; // Gold
    if (streak >= 7) return '#FF6B35'; // Orange
    if (streak >= 3) return '#4CAF50'; // Green
    return '#9E9E9E'; // Gray
  };

  const getAchievementIcon = (icon: string) => {
    return <span style={{ fontSize: '1.5rem' }}>{icon}</span>;
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          {getText('loading')}...
        </Typography>
      </Box>
    );
  }

  if (!userStats) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No sticker data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxHeight: '70vh', overflow: 'auto' }}>
      {/* Overview Section */}
      <Accordion
        expanded={expandedSection === 'overview'}
        onChange={() => setExpandedSection(expandedSection === 'overview' ? false : 'overview')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant='h6'>📊 {getText('overview')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
            <Card sx={{ textAlign: 'center', bgcolor: 'primary.light', color: 'primary.contrastText', minWidth: 200, flex: '1 1 200px' }}>
              <CardContent>
                <Typography variant="h3" component="div">
                  {userStats.totalStickers}
                </Typography>
                <Typography variant="body2">
                  {getText('totalStickers')}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ textAlign: 'center', bgcolor: 'success.light', color: 'success.contrastText', minWidth: 200, flex: '1 1 200px' }}>
              <CardContent>
                <Typography variant="h3" component="div">
                  {userStats.totalPlansCompleted}
                </Typography>
                <Typography variant="body2">
                  {getText('plansCompleted')}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ textAlign: 'center', bgcolor: 'warning.light', color: 'warning.contrastText', minWidth: 200, flex: '1 1 200px' }}>
              <CardContent>
                <Typography variant="h3" component="div">
                  {userStats.totalDaysCompleted}
                </Typography>
                <Typography variant="body2">
                  {getText('daysCompleted')}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ textAlign: 'center', bgcolor: getStreakColor(userStats.currentStreak), color: 'white', minWidth: 200, flex: '1 1 200px' }}>
              <CardContent>
                <Typography variant="h3" component="div" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <FireIcon />
                  {userStats.currentStreak}
                </Typography>
                <Typography variant="body2">
                  {getText('currentStreak')}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Achievements Section */}
      <Accordion
        expanded={expandedSection === 'achievements'}
        onChange={() => setExpandedSection(expandedSection === 'achievements' ? false : 'achievements')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant='h6'>🏆 {getText('achievements')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {userStats.achievements.length > 0 ? (
            <List>
              {userStats.achievements.map((achievement, index) => (
                <React.Fragment key={achievement.id}>
                  <ListItem>
                    <ListItemIcon>
                      {getAchievementIcon(achievement.icon)}
                    </ListItemIcon>
                    <ListItemText
                      primary={getTranslatedAchievementText(achievement.id, userLanguage).name}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" component="span">
                            {getTranslatedAchievementText(achievement.id, userLanguage).description}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" component="span">
                            {isChinese ? '解锁时间：' :
                              isSpanish ? 'Desbloqueado: ' :
                                isFrench ? 'Débloqué : ' :
                                  'Unlocked: '}{formatDate(achievement.unlockedAt)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < userStats.achievements.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              Complete more activities to unlock achievements!
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Recent Activity Section */}
      <Accordion
        expanded={expandedSection === 'activity'}
        onChange={() => setExpandedSection(expandedSection === 'activity' ? false : 'activity')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant='h6'>📈 {getText('recentActivity')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {userStats.recentStickerEvents.length > 0 ? (
            <List>
              {userStats.recentStickerEvents.slice(0, 10).map((event, index) => (
                <React.Fragment key={event.id}>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" component="span">
                            {event.description || `${event.stickersEarned} sticker${event.stickersEarned !== 1 ? 's' : ''} earned`}
                          </Typography>
                          {event.stickersEarned > 0 && (
                            <Chip
                              label={`+${event.stickersEarned}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" component="span">
                          {formatDate(event.timestamp)}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {index < Math.min(userStats.recentStickerEvents.length, 10) - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No recent activity. Complete some plan activities to see your progress here!
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export const UserProfileModal: React.FC<UserProfileModalProps> = ({
  open,
  onClose,
  userId,
  userLanguage,
  userDisplayName = 'Pet Owner',
  userEmail = 'pet@example.com',
  userPhotoURL,
  onUserUpdate,
  onStartOnboarding
}) => {
  // Debug logging for language detection
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_LOGS === 'true') console.log('🔍 UserProfileModal Language Debug:', {
    userLanguage,
    userDisplayName,
    userEmail
  });
  const [activeCategory, setActiveCategory] = useState<SettingsCategory>('general');
  const [petProfiles, setPetProfiles] = useState<PetProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingPet, setEditingPet] = useState<PetProfile | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [showEmbeddedEdit, setShowEmbeddedEdit] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Share functionality state
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [sharingPet, setSharingPet] = useState<PetProfile | null>(null);
  const [shareLink, setShareLink] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Profile editing state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editDisplayName, setEditDisplayName] = useState(userDisplayName);
  const [profilePhotoURL, setProfilePhotoURL] = useState(userPhotoURL || '');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [editIsProfessional, setEditIsProfessional] = useState(false);

  // Professional Intake State
  const [isProfessional, setIsProfessional] = useState(false);
  const [pendingIntakes, setPendingIntakes] = useState<PetProfile[]>([]);
  const [approvingIntake, setApprovingIntake] = useState<string | null>(null);
  const [viewingIntake, setViewingIntake] = useState<PetProfile | null>(null);

  // Language-specific content
  const getContent = () => {
    const content = {
      en: {
        title: "Settings",
        general: "General",
        profile: "Profile",
        pets: "My Pets",
        intake: "Professional Intake",
        generateIntakeLink: "Generate Intake Link",
        copyLink: "Copy Link",
        pendingIntakes: "Pending Intake Forms",
        approveIntake: "Approve Profile",
        view: "View",
        convertToProfile: "Convert to Profile",
        intakeDetails: "Intake Details",
        close: "Close",
        noPendingIntakes: "No pending intake forms",
        intakeLinkCopied: "Intake link copied!",
        intakeLinkDesc: "Send this link to clients so they can fill out an intake form.",
        statistics: "Statistics",
        notifications: "Notifications",
        security: "Security",
        data: "Data Controls",
        welcome: "Welcome",
        petCount: "Pets",
        noPets: "No pets added yet",
        addPet: "Add New Pet",
        editPet: "Edit Pet",
        viewProfile: "View Profile",
        deletePet: "Delete Pet",
        confirmDelete: "Are you sure you want to delete this pet profile?",
        loading: "Loading...",
        error: "Error loading data",
        generalDescription: "Manage your account settings and preferences",
        profileDescription: "Update your personal information",
        edit: "Edit",
        save: "Save",
        saving: "Saving...",
        cancel: "Cancel",
        displayNameLabel: "Display Name",
        emailLabel: "Email",
        photoLabel: "Profile Photo",
        uploadPhoto: "Upload Photo",
        removePhoto: "Remove Photo",
        uploading: "Uploading...",
        uploadingImage: "Uploading image, please wait...",
        selectPhoto: "Select Photo",
        nameRequired: "Display name cannot be empty",
        uploadError: "Failed to upload photo",
        saveError: "Failed to save profile",
        deleteError: "Failed to delete pet profile",
        permissionDenied: "Permission denied. Please check your Firebase Storage rules.",
        storageNotFound: "Storage bucket not found. Please check your Firebase configuration.",
        quotaExceeded: "Storage quota exceeded. Please try a smaller image.",
        photoTooLarge: "Profile photo is too large. Please try a smaller image.",
        invalidImage: "Please select a valid image file",
        imageTooLarge: "Image size must be less than 5MB",
        uploadTimeout: "Upload timeout after 30 seconds",
        emailCannotChange: "Email cannot be changed",
        low: "Low",
        moderate: "Moderate",
        high: "High",
        unknownBreed: "Unknown breed",
        unknownActivity: "Unknown activity",
        sharePetTitle: "Share Pet Profile",
        sharePetDescription: "Share this pet's profile with friends or pet care professionals",
        shareLinkLabel: "Share Link",
        copyButton: "Copy",
        copied: "Copied!",
        closeButton: "Close",
        feedback: "User Feedback",
        feedbackDescription: "Share your thoughts and suggestions to help us improve PetSnap",
        feedbackNameLabel: "Name",
        feedbackEmailLabel: "Email",
        feedbackMessageLabel: "Message",
        feedbackMessagePlaceholder: "Tell us what features should be improved, what new features you'd like to see, or any other feedback you have. Your input is valuable to us!",
        feedbackSubmitButton: "Submit Feedback",
        feedbackSubmitting: "Submitting...",
        feedbackSuccess: "Thank you for your feedback! We appreciate your input and will review it carefully.",
        feedbackError: "Failed to submit feedback. Please try again.",
        feedbackNameRequired: "Name is required",
        feedbackEmailRequired: "Email is required",
        feedbackMessageRequired: "Message is required",
        feedbackEmailInvalid: "Please enter a valid email address",
        favoriteThings: "Favorite Things",
        freshTreatPermission: "Fresh Treat Permission",
        pottyHabits: "Potty",
        barkingHabits: "Barking",
        chewingHabits: "Chewing",
        separationAnxiety: "Separation Anxiety",
        walkingPreferences: "Walking Preferences",
        boardingDates: "Boarding Dates",
        emergencyContact: "Emergency",
        vetContact: "Vet",
        behaviorHabits: "Behavior & Habits",
        generalInfo: "General Info",
        contacts: "Contacts",
        favoriteThingsOther: "Other Favorites",
        yes: "Yes",
        no: "No",
        to: "to"
      },
      'zh-CN': {
        title: "设置",
        general: "常规",
        profile: "个人资料",
        pets: "我的宠物",
        intake: "专业接收入口",
        generateIntakeLink: "生成接收链接",
        copyLink: "复制链接",
        pendingIntakes: "待处理接收表格",
        approveIntake: "批准资料",
        view: "查看",
        convertToProfile: "转换为档案",
        intakeDetails: "接收详情",
        close: "关闭",
        noPendingIntakes: "没有待处理接收表格",
        intakeLinkCopied: "接收链接已复制！",
        intakeLinkDesc: "将此链接发送给客户，以便他们填写接收表格。",
        statistics: "统计",
        notifications: "通知",
        security: "安全",
        data: "数据控制",
        welcome: "欢迎",
        petCount: "宠物",
        noPets: "还没有添加宠物",
        addPet: "添加新宠物",
        editPet: "编辑宠物",
        viewProfile: "查看资料",
        deletePet: "删除宠物",
        confirmDelete: "您确定要删除这个宠物资料吗？",
        loading: "加载中...",
        error: "加载数据时出错",
        generalDescription: "管理您的账户设置和偏好",
        profileDescription: "更新您的个人信息",
        edit: "编辑",
        save: "保存",
        saving: "保存中...",
        cancel: "取消",
        displayNameLabel: "显示名称",
        emailLabel: "邮箱",
        photoLabel: "头像",
        uploadPhoto: "上传照片",
        removePhoto: "删除照片",
        uploading: "上传中...",
        uploadingImage: "正在上传图片，请稍候...",
        selectPhoto: "选择照片",
        nameRequired: "显示名称不能为空",
        uploadError: "上传照片失败",
        saveError: "保存资料失败",
        deleteError: "删除宠物资料失败",
        permissionDenied: "权限被拒绝。请检查您的Firebase Storage规则。",
        storageNotFound: "未找到存储桶。请检查您的Firebase配置。",
        quotaExceeded: "存储配额已超限。请尝试更小的图片。",
        photoTooLarge: "头像太大。请尝试更小的图片。",
        invalidImage: "请选择有效的图片文件",
        imageTooLarge: "图片大小必须小于5MB",
        uploadTimeout: "上传超时，30秒后超时",
        emailCannotChange: "电子邮件无法更改",
        low: "低",
        moderate: "中等",
        high: "高",
        unknownBreed: "未知品种",
        unknownActivity: "未知活动水平",
        sharePetTitle: "分享宠物资料",
        sharePetDescription: "与朋友或宠物护理专业人士分享这个宠物的资料",
        shareLinkLabel: "分享链接",
        copyButton: "复制",
        copied: "已复制！",
        closeButton: "关闭",
        feedback: "用户反馈",
        feedbackDescription: "分享您的想法和建议，帮助我们改进PetSnap",
        feedbackNameLabel: "姓名",
        feedbackEmailLabel: "邮箱",
        feedbackMessageLabel: "消息",
        feedbackMessagePlaceholder: "告诉我们哪些功能需要改进，您希望看到什么新功能，或者您有任何其他反馈。您的意见对我们很有价值！",
        feedbackSubmitButton: "提交反馈",
        feedbackSubmitting: "提交中...",
        feedbackSuccess: "感谢您的反馈！我们很重视您的意见，会仔细审查。",
        feedbackError: "提交反馈失败，请重试。",
        feedbackNameRequired: "姓名是必填项",
        feedbackEmailRequired: "邮箱是必填项",
        feedbackMessageRequired: "消息是必填项",
        feedbackEmailInvalid: "请输入有效的邮箱地址",
        favoriteThings: "最爱的东西",
        freshTreatPermission: "生鲜零食许可",
        pottyHabits: "排便习惯",
        barkingHabits: "吠叫习惯",
        chewingHabits: "咬物习惯",
        separationAnxiety: "分离焦虑",
        walkingPreferences: "散步偏好",
        boardingDates: "寄养日期",
        emergencyContact: "紧急联系人",
        vetContact: "兽医联系人",
        behaviorHabits: "行为与习惯",
        generalInfo: "基本信息",
        contacts: "联系方式",
        favoriteThingsOther: "其他最爱",
        yes: "是",
        no: "否",
        to: "至"
      },
      es: {
        title: "Configuración",
        general: "General",
        profile: "Perfil",
        pets: "Mis Mascotas",
        intake: "Ingreso Profesional",
        generateIntakeLink: "Generar Enlace de Ingreso",
        copyLink: "Copiar Enlace",
        pendingIntakes: "Formularios Pendientes",
        approveIntake: "Aprobar Perfil",
        view: "Ver",
        convertToProfile: "Convertir a Perfil",
        intakeDetails: "Detalles de Ingreso",
        close: "Cerrar",
        noPendingIntakes: "No hay formularios pendientes",
        intakeLinkCopied: "¡Enlace copiado!",
        intakeLinkDesc: "Envía este enlace a los clientes para que puedan llenar un formulario de ingreso.",
        statistics: "Estadísticas",
        notifications: "Notificaciones",
        security: "Seguridad",
        data: "Controles de Datos",
        welcome: "Bienvenido",
        petCount: "Mascotas",
        noPets: "Aún no hay mascotas agregadas",
        addPet: "Agregar Nueva Mascota",
        editPet: "Editar Mascota",
        viewProfile: "Ver Perfil",
        deletePet: "Eliminar Mascota",
        confirmDelete: "¿Estás seguro de que quieres eliminar este perfil de mascota?",
        loading: "Cargando...",
        error: "Error al cargar los datos",
        generalDescription: "Gestiona la configuración y preferencias de tu cuenta",
        profileDescription: "Actualiza tu información personal",
        edit: "Editar",
        save: "Guardar",
        saving: "Guardando...",
        cancel: "Cancelar",
        displayNameLabel: "Nombre de Mostrar",
        emailLabel: "Correo Electrónico",
        photoLabel: "Foto de Perfil",
        uploadPhoto: "Subir Foto",
        removePhoto: "Eliminar Foto",
        uploading: "Subiendo...",
        uploadingImage: "Subiendo imagen, por favor espera...",
        selectPhoto: "Seleccionar foto",
        nameRequired: "El nombre de mostrar no puede estar vacío",
        uploadError: "Error al subir la foto",
        saveError: "Error al guardar el perfil",
        deleteError: "Error al eliminar el perfil de mascota",
        permissionDenied: "Permiso denegado. Por favor revisa las reglas de Firebase Storage.",
        storageNotFound: "Bucket de almacenamiento no encontrado. Por favor revisa tu configuración de Firebase.",
        quotaExceeded: "Cuota de almacenamiento excedida. Por favor intenta con una imagen más pequeña.",
        photoTooLarge: "La foto de perfil es muy grande. Por favor intenta con una imagen más pequeña.",
        invalidImage: "Por favor selecciona un archivo de imagen válido",
        imageTooLarge: "El tamaño de la imagen debe ser menor a 5MB",
        uploadTimeout: "Tiempo de espera de subida agotado después de 30 segundos",
        emailCannotChange: "El correo electrónico no se puede cambiar",
        low: "Bajo",
        moderate: "Moderado",
        high: "Alto",
        unknownBreed: "Raza desconocida",
        unknownActivity: "Actividad desconocida",
        sharePetTitle: "Compartir Perfil de Mascota",
        sharePetDescription: "Comparte el perfil de esta mascota con amigos o profesionales del cuidado de mascotas",
        shareLinkLabel: "Enlace de Compartir",
        copyButton: "Copiar",
        copied: "¡Copiado!",
        closeButton: "Cerrar",
        feedback: "Comentarios del Usuario",
        feedbackDescription: "Comparte tus pensamientos y sugerencias para ayudarnos a mejorar PetSnap",
        feedbackNameLabel: "Nombre",
        feedbackEmailLabel: "Correo Electrónico",
        feedbackMessageLabel: "Mensaje",
        feedbackMessagePlaceholder: "Dinos qué funciones deberían mejorarse, qué nuevas funciones te gustaría ver, o cualquier otro comentario que tengas. ¡Tu opinión es valiosa para nosotros!",
        feedbackSubmitButton: "Enviar Comentarios",
        feedbackSubmitting: "Enviando...",
        feedbackSuccess: "¡Gracias por tus comentarios! Apreciamos tu opinión y la revisaremos cuidadosamente.",
        feedbackError: "Error al enviar comentarios. Por favor, inténtalo de nuevo.",
        feedbackNameRequired: "El nombre es requerido",
        feedbackEmailRequired: "El correo electrónico es requerido",
        feedbackMessageRequired: "El mensaje es requerido",
        feedbackEmailInvalid: "Por favor, ingresa un correo electrónico válido",
        favoriteThings: "Cosas Favoritas",
        freshTreatPermission: "Permiso de Premios Frescos",
        pottyHabits: "Hábitos de Baño",
        barkingHabits: "Hábitos de Ladrido",
        chewingHabits: "Hábitos de Masticación",
        separationAnxiety: "Ansiedad por Separación",
        walkingPreferences: "Preferencias de Paseo",
        boardingDates: "Fechas de Hospedaje",
        emergencyContact: "Emergencia",
        vetContact: "Veterinario",
        behaviorHabits: "Comportamiento y Hábitos",
        generalInfo: "Información General",
        contacts: "Contactos",
        favoriteThingsOther: "Otros Favoritos",
        yes: "Sí",
        no: "No",
        to: "a"
      },
      fr: {
        title: "Paramètres",
        general: "Général",
        profile: "Profil",
        pets: "Mes Animaux",
        intake: "Admission Professionnelle",
        generateIntakeLink: "Générer un Lien d'Admission",
        copyLink: "Copier le Lien",
        pendingIntakes: "Formulaires en Attente",
        approveIntake: "Approuver le Profil",
        view: "Voir",
        convertToProfile: "Convertir en Profil",
        intakeDetails: "Détails d'Admission",
        close: "Fermer",
        noPendingIntakes: "Aucun formulaire en attente",
        intakeLinkCopied: "Lien copié !",
        intakeLinkDesc: "Envoyez ce lien à vos clients afin qu'ils remplissent un formulaire d'admission.",
        statistics: "Statistiques",
        notifications: "Notifications",
        security: "Sécurité",
        data: "Contrôles des Données",
        welcome: "Bienvenue",
        petCount: "Animaux",
        noPets: "Aucun animal ajouté pour le moment",
        addPet: "Ajouter un Nouvel Animal",
        editPet: "Modifier l'Animal",
        viewProfile: "Voir le Profil",
        deletePet: "Supprimer l'Animal",
        confirmDelete: "Êtes-vous sûr de vouloir supprimer ce profil d'animal ?",
        loading: "Chargement...",
        error: "Erreur lors du chargement des données",
        generalDescription: "Gérez les paramètres et préférences de votre compte",
        profileDescription: "Mettez à jour vos informations personnelles",
        edit: "Modifier",
        save: "Enregistrer",
        saving: "Enregistrement...",
        cancel: "Annuler",
        displayNameLabel: "Nom d'Affichage",
        emailLabel: "E-mail",
        photoLabel: "Photo de Profil",
        uploadPhoto: "Télécharger Photo",
        removePhoto: "Supprimer Photo",
        uploading: "Téléchargement...",
        uploadingImage: "Téléchargement de l'image, veuillez patienter...",
        selectPhoto: "Sélectionner photo",
        nameRequired: "Le nom d'affichage ne peut pas être vide",
        uploadError: "Échec du téléchargement de la photo",
        saveError: "Échec de la sauvegarde du profil",
        deleteError: "Échec de la suppression du profil d'animal",
        permissionDenied: "Permission refusée. Veuillez vérifier vos règles Firebase Storage.",
        storageNotFound: "Bucket de stockage introuvable. Veuillez vérifier votre configuration Firebase.",
        quotaExceeded: "Quota de stockage dépassé. Veuillez essayer une image plus petite.",
        photoTooLarge: "La photo de profil est trop grande. Veuillez essayer une image plus petite.",
        invalidImage: "Veuillez sélectionner un fichier image valide",
        imageTooLarge: "La taille de l'image doit être inférieure à 5MB",
        uploadTimeout: "Délai d'attente de téléchargement dépassé après 30 secondes",
        emailCannotChange: "L'e-mail ne peut pas être modifié",
        low: "Faible",
        moderate: "Modéré",
        high: "Élevé",
        unknownBreed: "Race inconnue",
        unknownActivity: "Activité inconnue",
        sharePetTitle: "Partager le Profil de l'Animal",
        sharePetDescription: "Partagez le profil de cet animal avec des amis ou des professionnels des soins aux animaux",
        shareLinkLabel: "Lien de Partage",
        copyButton: "Copier",
        copied: "Copié !",
        closeButton: "Fermer",
        feedback: "Commentaires Utilisateur",
        feedbackDescription: "Partagez vos pensées et suggestions pour nous aider à améliorer PetSnap",
        feedbackNameLabel: "Nom",
        feedbackEmailLabel: "Email",
        feedbackMessageLabel: "Message",
        feedbackMessagePlaceholder: "Dites-nous quelles fonctionnalités devraient être améliorées, quelles nouvelles fonctionnalités vous aimeriez voir, ou tout autre commentaire que vous avez. Votre avis nous est précieux !",
        feedbackSubmitButton: "Envoyer les Commentaires",
        feedbackSubmitting: "Envoi en cours...",
        feedbackSuccess: "Merci pour vos commentaires ! Nous apprécions votre avis et l'examinerons attentivement.",
        feedbackError: "Échec de l'envoi des commentaires. Veuillez réessayer.",
        feedbackNameRequired: "Le nom est requis",
        feedbackEmailRequired: "L'email est requis",
        feedbackMessageRequired: "Le message est requis",
        feedbackEmailInvalid: "Veuillez entrer une adresse email valide",
        favoriteThings: "Choses Préférées",
        freshTreatPermission: "Permission de Friandises Fraîches",
        pottyHabits: "Habitudes de Propreté",
        barkingHabits: "Habitudes d'Aboiement",
        chewingHabits: "Habitudes de Mastication",
        separationAnxiety: "Anxiété de Séparation",
        walkingPreferences: "Préférences de Marche",
        boardingDates: "Dates de Pension",
        emergencyContact: "Urgence",
        vetContact: "Vétérinaire",
        behaviorHabits: "Comportement et Habitudes",
        generalInfo: "Informations Générales",
        contacts: "Contacts",
        favoriteThingsOther: "Autres Favoris",
        yes: "Oui",
        no: "Non",
        to: "à"
      }
    };

    return content[userLanguage] || content.en;
  };

  const content = getContent();

  // Load pet profiles
  useEffect(() => {
    const loadPetProfiles = async () => {
      if (!open || !userId) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch user document to check if professional
        const userRef = doc(db, 'users', userId);
        const userSnap = await getDoc(userRef);
        const userData = userSnap.data();
        const userIsProfessional = userData?.isProfessional || false;
        setIsProfessional(userIsProfessional);

        const petProfilesRef = collection(db, 'users', userId, 'petProfiles');
        const petQuery = query(petProfilesRef, orderBy('createdAt', 'desc'));
        const petSnapshot = await getDocs(petQuery);

        const pets: PetProfile[] = [];
        petSnapshot.forEach((doc) => {
          pets.push({ id: doc.id, ...doc.data() } as PetProfile);
        });

        setPetProfiles(pets);

        // Fetch pending intakes if professional
        if (userIsProfessional) {
          await loadPendingIntakes();
        }
      } catch (error) {
        console.error('Error loading pet profiles:', error);
        setError(content.error);
      } finally {
        setLoading(false);
      }
    };

    loadPetProfiles();
  }, [open, userId, userLanguage]);

  // Calculate current age from birthday or use stored age
  const getCurrentAge = (pet: PetProfile): string => {
    console.log('🔍 getCurrentAge called for pet:', pet.name, {
      birthday: pet.birthday,
      type: typeof pet.birthday,
      isDate: pet.birthday instanceof Date,
      hasToDate: pet.birthday && typeof pet.birthday === 'object' && 'toDate' in pet.birthday
    });

    // Prioritize stored age if it is greater than 0
    if (pet.age && pet.age > 0) {
      return pet.age.toFixed(1);
    }

    if (pet.birthday) {
      try {
        // Handle different birthday formats
        let birthdayForCalculation: any;

        if (pet.birthday instanceof Date) {
          birthdayForCalculation = { toDate: () => pet.birthday as Date };
        } else if (typeof pet.birthday === 'string') {
          // If it's a string, try to parse it first
          const parsedDate = parseDateFromString(pet.birthday as string, userLanguage);
          if (parsedDate) {
            birthdayForCalculation = { toDate: () => parsedDate };
          } else {
            console.warn('❌ Failed to parse birthday string:', pet.birthday);
            return pet.age?.toFixed(1) || '0.0';
          }
        } else if (pet.birthday && typeof pet.birthday === 'object' && 'toDate' in pet.birthday) {
          // Firestore Timestamp
          birthdayForCalculation = pet.birthday;
        } else {
          console.warn('❌ Unknown birthday format:', pet.birthday);
          return pet.age?.toFixed(1) || '0.0';
        }

        const age = calculateAgeFromBirthday(birthdayForCalculation);
        console.log('📅 Calculated age:', age);
        return age.toFixed(1);
      } catch (error) {
        console.warn('❌ Error calculating age from birthday:', error);
        // Fallback to stored age if birthday calculation fails
        return pet.age?.toFixed(1) || '0.0';
      }
    }
    // If no birthday, use the stored age (from onboarding or manual input)
    console.log('📅 No birthday, using stored age:', pet.age);
    return pet.age?.toFixed(1) || '0.0';
  };

  // Format gender display
  const formatGender = (gender?: string): string => {
    const genderMap = {
      en: { male: 'Male', female: 'Female', unknown: 'Unknown' },
      'zh-CN': { male: '雄性', female: '雌性', unknown: '未知' },
      es: { male: 'Macho', female: 'Hembra', unknown: 'Desconocido' },
      fr: { male: 'Mâle', female: 'Femelle', unknown: 'Inconnu' }
    };
    return genderMap[userLanguage]?.[gender as keyof typeof genderMap.en] || gender || 'Unknown';
  };

  // Format pet type display
  const formatPetType = (type?: string): string => {
    if (!type) return 'Unknown';

    const typeMap = {
      en: { dog: 'Dog', cat: 'Cat' },
      'zh-CN': { dog: '狗', cat: '猫' },
      es: { dog: 'Perro', cat: 'Gato' },
      fr: { dog: 'Chien', cat: 'Chat' }
    };

    return typeMap[userLanguage]?.[type as keyof typeof typeMap.en] || type.toUpperCase();
  };

  // Handle pet editing
  const handleEditPet = (pet: PetProfile) => {
    setEditingPet(pet);
    setShowEmbeddedEdit(true);
  };

  // Handle back to pet list
  const handleBackToPets = () => {
    setShowEmbeddedEdit(false);
    setEditingPet(null);
  };

  // Handle pet save
  const handlePetSave = (updatedPet: PetProfile) => {
    setPetProfiles(petProfiles.map(pet =>
      pet.id === updatedPet.id ? updatedPet : pet
    ));
    setEditDialogOpen(false);
    setShowEmbeddedEdit(false);
    setEditingPet(null);
  };

  // Handle pet deletion
  const handleDeletePet = async (petId: string) => {
    if (window.confirm(content.confirmDelete)) {
      try {
        await deleteDoc(doc(db, 'users', userId, 'petProfiles', petId));
        setPetProfiles(petProfiles.filter(pet => pet.id !== petId));
      } catch (error) {
        console.error('Error deleting pet:', error);
        setError(content.deleteError);
      }
    }
  };

  // Handle intake approval
  const handleApproveIntake = async (formId: string) => {
    try {
      setApprovingIntake(formId);
      setError(null);
      const result = await IntakeService.approveIntakeForm(userId, formId);

      setPendingIntakes(prev => prev.filter(pet => pet.id !== formId));

      if (result.petData) {
        setPetProfiles([{ ...result.petData, id: result.petId, isIntakePending: false } as PetProfile, ...petProfiles]);
      }

      alert(content.approveIntake + " successful!");
    } catch (err: any) {
      console.error('Error approving intake:', err);
      setError(err.message || 'Failed to approve intake form');
    } finally {
      setApprovingIntake(null);
    }
  };

  const handleDeleteIntake = async (formId: string) => {
    if (window.confirm(userLanguage === 'zh-CN' ? '确定要删除此待处理表单吗？' : userLanguage === 'es' ? '¿Estás seguro de que quieres eliminar este formulario pendiente?' : userLanguage === 'fr' ? 'Êtes-vous sûr de vouloir supprimer ce formulaire en attente ?' : 'Are you sure you want to delete this pending intake form?')) {
      try {
        await IntakeService.deleteIntakeForm(userId, formId);
        setPendingIntakes(prev => prev.filter(pet => pet.id !== formId));
      } catch (err) {
        console.error('Error deleting intake form:', err);
        alert(content.error);
      }
    }
  };

  const loadPendingIntakes = async () => {
    try {
      const intakes = await IntakeService.getPendingIntakeForms(userId);
      setPendingIntakes(intakes);
    } catch (error) {
      console.error('Error fetching pending intakes:', error);
    }
  };

  // Share functionality handlers
  const handleSharePet = async (pet: PetProfile) => {
    try {
      setSharingPet(pet);
      setLoading(true);

      // Generate share link using the service
      const shareUrl = await PetShareService.generateShareLink(pet.id!, userId);
      setShareLink(shareUrl);
      setShareDialogOpen(true);
    } catch (error) {
      console.error('Error generating share link:', error);
      setError('Failed to generate share link');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPetProfile = async (pet: PetProfile) => {
    try {
      setLoading(true);
      const shareUrl = await PetShareService.generateShareLink(pet.id!, userId);
      const shareUrlWithLang = `${shareUrl}?lang=${userLanguage || 'en'}`;
      window.open(shareUrlWithLang, '_blank');
    } catch (error) {
      console.error('Error viewing pet profile:', error);
      setError('Failed to open pet profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
    setSharingPet(null);
    setShareLink('');
    setCopySuccess(false);
  };

  // Profile editing functions
  const handleStartEditProfile = () => {
    setIsEditingProfile(true);
    setEditDisplayName(userDisplayName);
    setProfilePhotoURL(userPhotoURL || '');
    setEditIsProfessional(isProfessional);
  };

  const handleCancelEditProfile = () => {
    setIsEditingProfile(false);
    setEditDisplayName(userDisplayName);
    setProfilePhotoURL(userPhotoURL || '');
    setEditIsProfessional(isProfessional);
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    console.log('Starting photo upload:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      userId: userId
    });

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError(content.invalidImage);
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setError(content.imageTooLarge);
      return;
    }

    setUploadingPhoto(true);
    setError(null);

    try {
      // Check if storage is available
      if (!storage) {
        throw new Error('Firebase Storage is not initialized');
      }

      // Convert file to compressed data URL as fallback
      const convertToCompressedDataURL = (file: File, maxWidth: number = 200, quality: number = 0.8): Promise<string> => {
        return new Promise((resolve, reject) => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const img = new Image();

          img.onload = () => {
            // Calculate new dimensions
            let { width, height } = img;
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }

            canvas.width = width;
            canvas.height = height;

            // Draw and compress
            ctx?.drawImage(img, 0, 0, width, height);
            const compressedDataURL = canvas.toDataURL('image/jpeg', quality);
            resolve(compressedDataURL);
          };

          img.onerror = reject;
          img.src = URL.createObjectURL(file);
        });
      };

      // Try Firebase Storage first with timeout
      try {
        const fileName = `${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
        const storageRef = ref(storage, `profile-photos/${userId}/${fileName}`);

        console.log('Uploading to storage path:', `profile-photos/${userId}/${fileName}`);

        // Add timeout to prevent hanging
        const uploadPromise = uploadBytes(storageRef, file);
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error(content.uploadTimeout)), 30000)
        );

        const snapshot = await Promise.race([uploadPromise, timeoutPromise]) as any;
        console.log('Upload completed, getting download URL...');

        const downloadURL = await getDownloadURL(snapshot.ref);
        console.log('Download URL obtained:', downloadURL);

        setProfilePhotoURL(downloadURL);
        setError(null);
        return;
      } catch (storageError: any) {
        console.warn('Firebase Storage upload failed, trying data URL fallback:', storageError);

        // Fallback to compressed data URL if Firebase Storage fails
        const compressedDataURL = await convertToCompressedDataURL(file, 200, 0.7);
        console.log('Using compressed data URL fallback');

        setProfilePhotoURL(compressedDataURL);
        setError(null);

        // Show a warning that the image is stored locally
        console.warn('Profile photo stored as compressed data URL (local storage only)');
      }

    } catch (error: any) {
      console.error('Error uploading photo:', error);

      // Provide more specific error messages
      let errorMessage = content.uploadError;
      if (error.code === 'storage/unauthorized') {
        errorMessage = content.permissionDenied;
      } else if (error.code === 'storage/object-not-found') {
        errorMessage = content.storageNotFound;
      } else if (error.code === 'storage/quota-exceeded') {
        errorMessage = content.quotaExceeded;
      } else if (error.message) {
        errorMessage = `Upload failed: ${error.message}`;
      }

      setError(errorMessage);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!editDisplayName.trim()) {
      setError(content.nameRequired);
      return;
    }

    setSavingProfile(true);
    try {
      // Check if photo URL is too long for Firebase Auth (limit is ~2048 characters)
      const isPhotoURLTooLong = profilePhotoURL && profilePhotoURL.length > 2000;
      let authPhotoURL = '';
      let firestorePhotoURL = profilePhotoURL;

      if (profilePhotoURL) {
        if (isPhotoURLTooLong) {
          // Store the full photo URL in Firestore only
          authPhotoURL = ''; // Don't store in Auth
          firestorePhotoURL = profilePhotoURL;
          console.log('Photo URL too long for Auth, storing in Firestore only');
        } else {
          // Store in both Auth and Firestore
          authPhotoURL = profilePhotoURL;
          firestorePhotoURL = profilePhotoURL;
        }
      }

      // Update Firebase Auth profile (only if photo URL is short enough)
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, {
          displayName: editDisplayName.trim(),
          photoURL: authPhotoURL
        });
      }

      // Update or create Firestore user document (always store full photo URL here)
      const userRef = doc(db, 'users', userId);

      try {
        // Try to update first
        await updateDoc(userRef, {
          displayName: editDisplayName.trim(),
          photoURL: firestorePhotoURL || null,
          isProfessional: editIsProfessional,
          updatedAt: new Date()
        });
      } catch (error: any) {
        // If document doesn't exist, create it
        if (error.code === 'not-found') {
          console.log('User document not found, creating new one');
          await setDoc(userRef, {
            displayName: editDisplayName.trim(),
            photoURL: firestorePhotoURL || null,
            email: userEmail,
            isProfessional: editIsProfessional,
            createdAt: new Date(),
            updatedAt: new Date()
          });
        } else {
          throw error; // Re-throw if it's a different error
        }
      }

      // Update local state to immediately show/hide the intake tab
      setIsProfessional(editIsProfessional);

      // Notify parent component
      if (onUserUpdate) {
        onUserUpdate({
          displayName: editDisplayName.trim(),
          photoURL: firestorePhotoURL || undefined
        });
      }

      setIsEditingProfile(false);
      setError(null);
    } catch (error: any) {
      console.error('Error saving profile:', error);

      // Provide more specific error messages
      let errorMessage = content.saveError;
      if (error.code === 'auth/invalid-profile-attribute') {
        errorMessage = content.photoTooLarge;
      } else if (error.message) {
        errorMessage = `Save failed: ${error.message}`;
      }

      setError(errorMessage);
    } finally {
      setSavingProfile(false);
    }
  };

  // Settings categories
  const categories: { id: SettingsCategory; label: string; icon: any }[] = [
    { id: 'general', label: content.general, icon: SettingsIcon },
    { id: 'profile', label: content.profile, icon: PersonIcon },
    { id: 'pets', label: content.pets, icon: PetsIcon },
    ...(isProfessional ? [{ id: 'intake' as SettingsCategory, label: content.intake, icon: AssignmentIcon }] : []),
    { id: 'progress', label: userLanguage === 'zh-CN' ? '进度' : userLanguage === 'es' ? 'Progreso' : userLanguage === 'fr' ? 'Progrès' : 'Progress', icon: TimelineIcon },
    { id: 'statistics', label: content.statistics, icon: AnalyticsIcon },
    { id: 'notifications', label: content.notifications, icon: NotificationsIcon },
    { id: 'security', label: content.security, icon: SecurityIcon },
    { id: 'data', label: content.data, icon: DataIcon },
    { id: 'feedback', label: content.feedback, icon: FeedbackIcon }
  ];

  // Render content based on active category
  const renderCategoryContent = () => {
    switch (activeCategory) {
      case 'general':
        return (
          <Box>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              gutterBottom
              sx={{ fontWeight: 600, mb: 3 }}
            >
              {content.general}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              paragraph
              sx={{
                fontSize: isMobile ? '0.9rem' : '1rem',
                lineHeight: 1.6,
                mb: 3
              }}
            >
              {content.generalDescription}
            </Typography>

            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 3,
              flexDirection: isMobile ? 'column' : 'row',
              textAlign: isMobile ? 'center' : 'left'
            }}>
              <Avatar
                sx={{
                  width: isMobile ? 50 : 60,
                  height: isMobile ? 50 : 60,
                  bgcolor: 'primary.main'
                }}
                src={profilePhotoURL || userPhotoURL}
              >
                {!profilePhotoURL && !userPhotoURL && <PersonIcon />}
              </Avatar>
              <Box>
                <Typography variant={isMobile ? "subtitle1" : "h6"}>{userDisplayName}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{
                  fontSize: isMobile ? '0.8rem' : '0.875rem'
                }}>
                  {userEmail}
                </Typography>
                <Chip
                  label={`${petProfiles.length} ${content.petCount}`}
                  color="primary"
                  size="small"
                  sx={{ mt: 1 }}
                />
              </Box>
            </Box>
          </Box>
        );

      case 'profile':
        return (
          <Box>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 2 : 0
            }}>
              <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 600 }}>
                {content.profile}
              </Typography>
              {!isEditingProfile && (
                <Button
                  startIcon={<EditIcon />}
                  onClick={handleStartEditProfile}
                  variant="outlined"
                  size={isMobile ? "medium" : "small"}
                  fullWidth={isMobile}
                  sx={{ minHeight: isMobile ? 44 : 'auto' }}
                >
                  {content.edit}
                </Button>
              )}
            </Box>

            <Typography
              variant="body1"
              color="text.secondary"
              paragraph
              sx={{
                fontSize: isMobile ? '0.9rem' : '1rem',
                lineHeight: 1.6,
                mb: 3
              }}
            >
              {content.profileDescription}
            </Typography>

            <Box sx={{ maxWidth: isMobile ? '100%' : 500 }}>
              {/* Profile Photo Section */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="subtitle2"
                  color="text.primary"
                  sx={{
                    mb: 2,
                    fontWeight: 600,
                    fontSize: isMobile ? '0.9rem' : '1rem'
                  }}
                >
                  {content.photoLabel}
                </Typography>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexDirection: isMobile ? 'column' : 'row'
                }}>
                  <Avatar
                    sx={{
                      width: isMobile ? 60 : 80,
                      height: isMobile ? 60 : 80,
                      bgcolor: 'primary.main'
                    }}
                    src={isEditingProfile ? profilePhotoURL : (profilePhotoURL || userPhotoURL)}
                  >
                    {!profilePhotoURL && !userPhotoURL && <PersonIcon />}
                  </Avatar>
                  {isEditingProfile && (
                    <Box>
                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="profile-photo-upload"
                        type="file"
                        onChange={handlePhotoUpload}
                        disabled={uploadingPhoto}
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <label htmlFor="profile-photo-upload">
                          <Button
                            component="span"
                            variant="outlined"
                            startIcon={uploadingPhoto ? <CircularProgress size={16} /> : <PhotoCameraIcon />}
                            disabled={uploadingPhoto}
                            sx={{ mb: 1 }}
                          >
                            {uploadingPhoto ? content.uploading : content.selectPhoto}
                          </Button>
                        </label>
                        {uploadingPhoto && (
                          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                            {content.uploadingImage}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Display Name Section */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="subtitle2"
                  color="text.primary"
                  sx={{
                    mb: 2,
                    fontWeight: 600,
                    fontSize: isMobile ? '0.9rem' : '1rem'
                  }}
                >
                  {content.displayNameLabel}
                </Typography>
                {isEditingProfile ? (
                  <TextField
                    fullWidth
                    value={editDisplayName}
                    onChange={(e) => setEditDisplayName(e.target.value)}
                    placeholder={userLanguage === 'zh-CN' ? '输入显示名称' :
                      userLanguage === 'es' ? 'Ingrese nombre para mostrar' :
                        userLanguage === 'fr' ? 'Entrez le nom d\'affichage' :
                          'Enter display name'}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: isMobile ? '16px' : '14px', // Prevent zoom on iOS
                      },
                    }}
                  />
                ) : (
                  <Typography variant="body1">{userDisplayName}</Typography>
                )}
              </Box>

              {/* Email Section (Read-only) */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="subtitle2"
                  color="text.primary"
                  sx={{
                    mb: 2,
                    fontWeight: 600,
                    fontSize: isMobile ? '0.9rem' : '1rem'
                  }}
                >
                  {content.emailLabel}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.primary"
                  sx={{
                    mb: 1,
                    fontSize: isMobile ? '0.9rem' : '1rem',
                    fontWeight: 500
                  }}
                >
                  {userEmail}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: isMobile ? '0.75rem' : '0.8rem' }}
                >
                  {content.emailCannotChange}
                </Typography>
              </Box>

              {/* isProfessional Checkbox Section */}
              <Box sx={{ mb: 4 }}>
                {isEditingProfile ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editIsProfessional}
                        onChange={(e) => setEditIsProfessional(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={userLanguage === 'zh-CN' ? '我是一名宠物专业人士' : userLanguage === 'es' ? 'Soy un profesional de mascotas' : userLanguage === 'fr' ? 'Je suis un professionnel des animaux' : 'I am a pet professional'}
                  />
                ) : (
                  <>
                    {isProfessional && (
                      <Chip
                        icon={<AssignmentIcon />}
                        label={userLanguage === 'zh-CN' ? '宠物专业人士' : userLanguage === 'es' ? 'Profesional de Mascotas' : userLanguage === 'fr' ? 'Professionnel des Animaux' : 'Pet Professional'}
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    )}
                  </>
                )}
              </Box>

              {/* Error Display */}
              {error && (
                <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Action Buttons */}
              {isEditingProfile && (
                <Box sx={{
                  display: 'flex',
                  gap: 2,
                  mt: 3,
                  flexDirection: isMobile ? 'column' : 'row'
                }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveProfile}
                    disabled={savingProfile || !editDisplayName.trim() || uploadingPhoto}
                    fullWidth={isMobile}
                    sx={{ minHeight: isMobile ? 44 : 'auto' }}
                  >
                    {savingProfile ? content.saving : content.save}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancelEditProfile}
                    disabled={savingProfile || uploadingPhoto}
                    fullWidth={isMobile}
                    sx={{ minHeight: isMobile ? 44 : 'auto' }}
                  >
                    {content.cancel}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        );

      case 'pets':
        // Show embedded edit form if editing a pet
        if (showEmbeddedEdit && editingPet) {
          return (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <IconButton
                  onClick={handleBackToPets}
                  sx={{ mr: 2 }}
                >
                  <CancelIcon />
                </IconButton>
                <Typography variant="h6">
                  {userLanguage === 'zh-CN' ? '编辑宠物信息' :
                    userLanguage === 'es' ? 'Editar información de mascota' :
                      userLanguage === 'fr' ? 'Modifier les informations de l\'animal' :
                        'Edit Pet Information'}
                </Typography>
              </Box>

              {/* Embedded Pet Edit Form */}
              <PetEditDialog
                open={true}
                pet={editingPet}
                userId={userId}
                userLanguage={userLanguage}
                onClose={handleBackToPets}
                onSave={handlePetSave}
                embedded={true}
              />
            </Box>
          );
        }

        return (
          <Box>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 2 : 0
            }}>
              <Typography
                variant={isMobile ? "h6" : "h5"}
                sx={{ fontWeight: 600 }}
              >
                {content.pets}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  onClose(); // Close the settings modal first
                  if (onStartOnboarding) {
                    onStartOnboarding(); // Trigger the onboarding wizard
                  }
                }}
                size={isMobile ? "medium" : "small"}
                fullWidth={isMobile}
                sx={{ minHeight: isMobile ? 44 : 'auto' }}
              >
                {content.addPet}
              </Button>
            </Box>

            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                <CircularProgress size={40} />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  {content.loading}
                </Typography>
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : petProfiles.length === 0 ? (
              <Box textAlign="center" py={4}>
                <PetsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  {content.noPets}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {petProfiles.map((pet) => (
                  <Box key={pet.id} sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: isMobile ? 1.5 : 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? 1.5 : 2,
                    flexDirection: isMobile ? 'column' : 'row'
                  }}>
                    {pet.profilePhoto && (
                      <Box
                        component="img"
                        src={pet.profilePhoto}
                        alt={pet.name}
                        sx={{
                          width: isMobile ? 50 : 60,
                          height: isMobile ? 50 : 60,
                          borderRadius: 1,
                          objectFit: 'cover'
                        }}
                      />
                    )}
                    <Box sx={{ flex: 1, width: isMobile ? '100%' : 'auto' }}>
                      <Typography
                        variant={isMobile ? "subtitle1" : "h6"}
                        gutterBottom
                        sx={{ textAlign: isMobile ? 'center' : 'left' }}
                      >
                        {pet.name}
                      </Typography>
                      <Box sx={{
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                        mb: 1,
                        justifyContent: isMobile ? 'center' : 'flex-start'
                      }}>
                        <Chip
                          label={formatPetType(pet.type)}
                          color="primary"
                          size="small"
                        />
                        <Chip
                          label={formatGender(pet.gender)}
                          color="secondary"
                          size="small"
                        />
                        <Chip
                          label={`${getCurrentAge(pet)} years`}
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{
                        textAlign: isMobile ? 'center' : 'left',
                        fontSize: isMobile ? '0.8rem' : '0.875rem'
                      }}>
                        {pet.breed || content.unknownBreed}{pet.activityLevel && content[pet.activityLevel] ? ` • ${content[pet.activityLevel]}` : ''}
                      </Typography>
                    </Box>
                    <Box sx={{
                      display: 'flex',
                      gap: 1,
                      justifyContent: isMobile ? 'center' : 'flex-end',
                      width: isMobile ? '100%' : 'auto'
                    }}>
                      <IconButton
                        size="small"
                        onClick={() => handleViewPetProfile(pet)}
                        sx={{ minWidth: 44, minHeight: 44 }}
                        title={content.viewProfile}
                      >
                        <ViewIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEditPet(pet)}
                        sx={{ minWidth: 44, minHeight: 44 }}
                        title={content.editPet}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleSharePet(pet)}
                        sx={{ minWidth: 44, minHeight: 44 }}
                        color="primary"
                        title={content.sharePetTitle}
                      >
                        <ShareIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeletePet(pet.id!)}
                        color="error"
                        sx={{ minWidth: 44, minHeight: 44 }}
                        title={content.deletePet}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        );

      case 'intake':
        return (
          <Box>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 2 : 0
            }}>
              <Typography
                variant={isMobile ? "h6" : "h5"}
                sx={{ fontWeight: 600 }}
              >
                {content.intake}
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {content.intakeLinkDesc}
            </Typography>

            <Box sx={{ mb: 4, display: 'flex', alignItems: 'stretch' }}>
              <TextField
                fullWidth
                variant="outlined"
                value={IntakeService.generateIntakeLink(userId)}
                inputProps={{
                  style: {
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                  },
                  readOnly: true
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    bgcolor: 'background.paper',
                    '& fieldset': {
                      borderRight: 'none'
                    }
                  }
                }}
              />
              <Button
                variant="contained"
                startIcon={<ShareIcon />}
                onClick={async () => {
                  try {
                    const link = IntakeService.generateIntakeLink(userId);
                    await navigator.clipboard.writeText(link);
                    alert(content.intakeLinkCopied);
                  } catch (e) {
                    console.error('Failed to copy intake link', e);
                  }
                }}
                sx={{
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  whiteSpace: 'nowrap',
                  px: isMobile ? 2 : 4,
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: 'none'
                  }
                }}
              >
                {content.copyLink}
              </Button>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 4 }}>
              <Typography variant="h6">
                {content.pendingIntakes}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<SyncIcon />}
                onClick={loadPendingIntakes}
              >
                {userLanguage === 'zh-CN' ? '刷新' : userLanguage === 'es' ? 'Actualizar' : userLanguage === 'fr' ? 'Rafraîchir' : 'Refresh'}
              </Button>
            </Box>

            {pendingIntakes.length === 0 ? (
              <Box textAlign="center" py={4}>
                <AssignmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  {content.noPendingIntakes}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {pendingIntakes.map((pet) => (
                  <Box key={pet.id} sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: isMobile ? 1.5 : 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? 1.5 : 2,
                    flexDirection: isMobile ? 'column' : 'row'
                  }}>
                    <Box sx={{ flex: 1, width: isMobile ? '100%' : 'auto' }}>
                      <Typography
                        variant={isMobile ? "subtitle1" : "h6"}
                        gutterBottom
                        sx={{ textAlign: isMobile ? 'center' : 'left' }}
                      >
                        {pet.name}
                      </Typography>
                      <Box sx={{
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                        mb: 1,
                        justifyContent: isMobile ? 'center' : 'flex-start'
                      }}>
                        <Chip
                          label={formatPetType(pet.type)}
                          color="primary"
                          size="small"
                        />
                        <Chip
                          label={formatGender(pet.gender)}
                          color="secondary"
                          size="small"
                        />
                        <Chip
                          label={`${getCurrentAge(pet)} years`}
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{
                        textAlign: isMobile ? 'center' : 'left',
                        fontSize: isMobile ? '0.8rem' : '0.875rem'
                      }}>
                        {pet.breed || content.unknownBreed}
                      </Typography>
                    </Box>
                    <Box sx={{
                      display: 'flex',
                      gap: 1,
                      justifyContent: isMobile ? 'center' : 'flex-end',
                      width: isMobile ? '100%' : 'auto'
                    }}>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDeleteIntake(pet.id!)}
                      >
                        {userLanguage === 'zh-CN' ? '删除' : userLanguage === 'es' ? 'Eliminar' : userLanguage === 'fr' ? 'Supprimer' : 'Delete'}
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={() => setViewingIntake(pet)}
                      >
                        {content.view}
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {/* View Intake Dialog */}
            <Dialog open={!!viewingIntake} onClose={() => setViewingIntake(null)} maxWidth="sm" fullWidth>
              <DialogTitle sx={{ fontWeight: 'bold' }}>{content.intakeDetails}</DialogTitle>
              <DialogContent dividers>
                {viewingIntake && (
                  <Box>
                    <Typography variant="h5" color="primary" gutterBottom>{viewingIntake.name}</Typography>
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                      {formatPetType(viewingIntake.type)} • {viewingIntake.breed || content.unknownBreed} • {getCurrentAge(viewingIntake)} years
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>Medical Status</Typography>
                    <Typography variant="body2">
                      Spayed/Neutered: {viewingIntake.medicalStatus?.isSpayedNeutered ? 'Yes' : 'No'}
                    </Typography>

                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>Food & Allergies</Typography>
                    <Typography variant="body2">
                      Has Allergies: {viewingIntake.foodAllergies?.hasAllergies ? 'Yes' : 'No'}
                    </Typography>
                    {viewingIntake.foodAllergies?.hasAllergies && (
                      <Typography variant="body2" color="error">
                        Details: {viewingIntake.foodAllergies.details}
                      </Typography>
                    )}
                    <Typography variant="body2">
                      {content.freshTreatPermission}: {viewingIntake.freshTreatPermission ? content.yes : content.no}
                    </Typography>

                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>{content.behaviorHabits}</Typography>
                    {viewingIntake.pottyHabits && (
                      <Typography variant="body2">{content.pottyHabits}: {viewingIntake.pottyHabits}</Typography>
                    )}
                    {viewingIntake.barkingHabits && (
                      <Typography variant="body2">{content.barkingHabits}: {viewingIntake.barkingHabits}</Typography>
                    )}
                    {viewingIntake.chewingHabits?.hasHabit && (
                      <Typography variant="body2">{content.chewingHabits}: {viewingIntake.chewingHabits.details || content.yes}</Typography>
                    )}
                    {viewingIntake.separationAnxiety && (
                      <Typography variant="body2">{content.separationAnxiety}: {viewingIntake.separationAnxiety}</Typography>
                    )}
                    {viewingIntake.favoriteThings && viewingIntake.favoriteThings.length > 0 && (
                      <Typography variant="body2">{content.favoriteThings}: {viewingIntake.favoriteThings.join(', ')}</Typography>
                    )}
                    {viewingIntake.favoriteThingsOther && (
                      <Typography variant="body2">{content.favoriteThingsOther}: {viewingIntake.favoriteThingsOther}</Typography>
                    )}
                    {viewingIntake.walkingPreferences && (
                      <Typography variant="body2">{content.walkingPreferences}: {viewingIntake.walkingPreferences}</Typography>
                    )}

                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>{content.generalInfo}</Typography>
                    {viewingIntake.boardingDates && (
                      <Typography variant="body2">{content.boardingDates}: {viewingIntake.boardingDates.from} {content.to} {viewingIntake.boardingDates.to}</Typography>
                    )}

                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>{content.contacts}</Typography>
                    {viewingIntake.emergencyContact && (
                      <Typography variant="body2">{content.emergencyContact}: {viewingIntake.emergencyContact}</Typography>
                    )}
                    {viewingIntake.vetContact && (
                      <Typography variant="body2">{content.vetContact}: {viewingIntake.vetContact}</Typography>
                    )}
                  </Box>
                )}
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button variant="outlined" onClick={() => setViewingIntake(null)}>
                  {content.close}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!viewingIntake || approvingIntake === viewingIntake.id}
                  onClick={async () => {
                    if (viewingIntake?.id) {
                      await handleApproveIntake(viewingIntake.id);
                      setViewingIntake(null);
                    }
                  }}
                >
                  {approvingIntake === viewingIntake?.id ? <CircularProgress size={20} color="inherit" /> : content.convertToProfile}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        );

      case 'progress':
        return (
          <Box>
            <AnalyticsDashboard userId={userId} userLanguage={userLanguage} />
          </Box>
        );

      case 'statistics':
        return (
          <Box>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              gutterBottom
              sx={{ fontWeight: 600, mb: 3 }}
            >
              {userLanguage === 'zh-CN' ? '贴纸统计' :
                userLanguage === 'es' ? 'Estadísticas de Calcomanías' :
                  userLanguage === 'fr' ? 'Statistiques d\'Autocollants' :
                    'Sticker Statistics'}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              paragraph
              sx={{
                fontSize: isMobile ? '0.9rem' : '1rem',
                lineHeight: 1.6,
                mb: 3
              }}
            >
              {userLanguage === 'zh-CN' ? '跟踪您在所有宠物训练计划中的进度和成就。' :
                userLanguage === 'es' ? 'Rastrea tu progreso y logros en todos tus planes de entrenamiento de mascotas.' :
                  userLanguage === 'fr' ? 'Suivez vos progrès et réalisations dans tous vos plans d\'entraînement d\'animaux.' :
                    'Track your progress and achievements across all your pet training plans.'}
            </Typography>

            {/* Embedded Statistics Content */}
            <EmbeddedStickerStats userId={userId} userLanguage={userLanguage} />
          </Box>
        );

      case 'notifications':
        return (
          <Box>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              gutterBottom
              sx={{ fontWeight: 600, mb: 3 }}
            >
              {content.notifications}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              paragraph
              sx={{
                fontSize: isMobile ? '0.9rem' : '1rem',
                lineHeight: 1.6,
                mb: 3
              }}
            >
              {userLanguage === 'zh-CN' ? '管理您的通知偏好' :
                userLanguage === 'es' ? 'Gestiona tus preferencias de notificación' :
                  userLanguage === 'fr' ? 'Gérez vos préférences de notification' :
                    'Manage your notification preferences'}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: isMobile ? '0.85rem' : '0.9rem',
                fontStyle: 'italic'
              }}
            >
              {userLanguage === 'zh-CN' ? '通知设置即将推出' :
                userLanguage === 'es' ? 'Configuración de notificaciones próximamente' :
                  userLanguage === 'fr' ? 'Paramètres de notification bientôt disponibles' :
                    'Notification settings coming soon'}
            </Typography>
          </Box>
        );

      case 'security':
        return (
          <Box>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              gutterBottom
              sx={{ fontWeight: 600, mb: 3 }}
            >
              {content.security}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              paragraph
              sx={{
                fontSize: isMobile ? '0.9rem' : '1rem',
                lineHeight: 1.6,
                mb: 3
              }}
            >
              {userLanguage === 'zh-CN' ? '管理您的安全设置' :
                userLanguage === 'es' ? 'Gestiona tu configuración de seguridad' :
                  userLanguage === 'fr' ? 'Gérez vos paramètres de sécurité' :
                    'Manage your security settings'}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: isMobile ? '0.85rem' : '0.9rem',
                fontStyle: 'italic'
              }}
            >
              {userLanguage === 'zh-CN' ? '安全设置即将推出' :
                userLanguage === 'es' ? 'Configuración de seguridad próximamente' :
                  userLanguage === 'fr' ? 'Paramètres de sécurité bientôt disponibles' :
                    'Security settings coming soon'}
            </Typography>
          </Box>
        );

      case 'data':
        return (
          <Box>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              gutterBottom
              sx={{ fontWeight: 600, mb: 3 }}
            >
              {content.data}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              paragraph
              sx={{
                fontSize: isMobile ? '0.9rem' : '1rem',
                lineHeight: 1.6,
                mb: 3
              }}
            >
              {userLanguage === 'zh-CN' ? '管理您的数据和控制' :
                userLanguage === 'es' ? 'Gestiona tus datos y controles' :
                  userLanguage === 'fr' ? 'Gérez vos données et contrôles' :
                    'Manage your data and controls'}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: isMobile ? '0.85rem' : '0.9rem',
                fontStyle: 'italic'
              }}
            >
              {userLanguage === 'zh-CN' ? '数据控制即将推出' :
                userLanguage === 'es' ? 'Controles de datos próximamente' :
                  userLanguage === 'fr' ? 'Contrôles des données bientôt disponibles' :
                    'Data controls coming soon'}
            </Typography>
          </Box>
        );

      case 'feedback':
        return <FeedbackForm
          userDisplayName={userDisplayName}
          userEmail={userEmail}
          userLanguage={userLanguage}
          isMobile={isMobile}
          userId={userId}
        />;

      default:
        return null;
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            height: isMobile ? '100vh' : '80vh',
            maxHeight: isMobile ? '100vh' : '800px',
            borderRadius: isMobile ? 0 : 2,
            bgcolor: 'background.paper'
          }
        }}
      >
        <DialogContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
          {isMobile ? (
            // Mobile: Tab Navigation Layout
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {/* Mobile Header */}
              <Box
                className="mobile-header-safe-area"
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.default',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingBottom: '16px'
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {content.title}
                </Typography>
                <IconButton size="small" onClick={onClose}>
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Mobile Tab Navigation */}
              <Box sx={{
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'background.default'
              }}>
                <Tabs
                  value={activeCategory}
                  onChange={(_, newValue) => setActiveCategory(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    '& .MuiTab-root': {
                      minHeight: 48,
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      textTransform: 'none',
                      px: 1.5,
                    },
                    '& .MuiTabs-indicator': {
                      height: 3,
                      borderRadius: '3px 3px 0 0',
                    }
                  }}
                >
                  {categories.map((category) => {
                    const IconComponent = category.icon;
                    return (
                      <Tab
                        key={category.id}
                        value={category.id}
                        icon={<IconComponent fontSize="small" />}
                        label={category.label}
                        iconPosition="start"
                        sx={{
                          minWidth: 'auto',
                          px: 1,
                          '&.Mui-selected': {
                            color: 'primary.main',
                          },
                        }}
                      />
                    );
                  })}
                </Tabs>
              </Box>

              {/* Mobile User Info */}
              <Box sx={{
                p: 1.5,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'background.default'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{
                    width: 28,
                    height: 28,
                    bgcolor: 'primary.main'
                  }}>
                    <PersonIcon fontSize="small" />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap fontSize="0.8rem">
                      {userDisplayName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap fontSize="0.7rem">
                      {userEmail}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Mobile Content Area */}
              <Box sx={{
                flex: 1,
                p: 2,
                overflow: 'auto',
                bgcolor: 'background.paper'
              }}>
                {renderCategoryContent()}
              </Box>
            </Box>
          ) : (
            // Desktop: Sidebar + Content Layout
            <>
              {/* Left Sidebar */}
              <Box sx={{
                width: 280,
                height: '100%',
                borderRight: 1,
                borderColor: 'divider',
                bgcolor: 'background.default',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {/* Header */}
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                      {content.title}
                    </Typography>
                    <IconButton size="small" onClick={onClose}>
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </Box>

                {/* Navigation */}
                <List sx={{ flex: 1, py: 1 }}>
                  {categories.map((category) => {
                    const IconComponent = category.icon;
                    return (
                      <ListItem key={category.id} disablePadding>
                        <ListItemButton
                          selected={activeCategory === category.id}
                          onClick={() => setActiveCategory(category.id)}
                          sx={{
                            mx: 1,
                            borderRadius: 1,
                            py: 1.5,
                            '&.Mui-selected': {
                              bgcolor: 'primary.main',
                              color: 'primary.contrastText',
                              '&:hover': {
                                bgcolor: 'primary.dark',
                              },
                              '& .MuiListItemIcon-root': {
                                color: 'primary.contrastText',
                              },
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <IconComponent fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={category.label}
                            primaryTypographyProps={{
                              fontSize: '0.9rem',
                              fontWeight: activeCategory === category.id ? 600 : 400
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>

                {/* User Info Footer */}
                <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{
                      width: 32,
                      height: 32,
                      bgcolor: 'primary.main'
                    }}>
                      <PersonIcon fontSize="small" />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap fontSize="0.875rem">
                        {userDisplayName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap fontSize="0.75rem">
                        {userEmail}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Right Content Area */}
              <Box sx={{
                flex: 1,
                p: 3,
                overflow: 'auto',
                bgcolor: 'background.paper'
              }}>
                {renderCategoryContent()}
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Pet Dialog */}
      <PetEditDialog
        open={editDialogOpen}
        pet={editingPet}
        userId={userId}
        userLanguage={userLanguage}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingPet(null);
        }}
        onSave={handlePetSave}
      />

      {/* Share Pet Dialog */}
      <Dialog
        open={shareDialogOpen}
        onClose={handleCloseShareDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {content.sharePetTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {content.sharePetDescription}
            </Typography>
          </Box>

          {sharingPet && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              mb: 3
            }}>
              {sharingPet.profilePhoto && (
                <Box
                  component="img"
                  src={sharingPet.profilePhoto}
                  alt={sharingPet.name}
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: 1,
                    objectFit: 'cover'
                  }}
                />
              )}
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" gutterBottom>
                  {sharingPet.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={formatPetType(sharingPet.type)}
                    color="primary"
                    size="small"
                  />
                  <Chip
                    label={formatGender(sharingPet.gender)}
                    color="secondary"
                    size="small"
                  />
                  <Chip
                    label={`${getCurrentAge(sharingPet)} years`}
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Box>
            </Box>
          )}

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              {content.shareLinkLabel}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                value={shareLink}
                InputProps={{ readOnly: true }}
                variant="outlined"
                size="small"
                sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
              />
              <Button
                variant="contained"
                onClick={handleCopyLink}
                disabled={copySuccess}
                sx={{ minWidth: 100 }}
              >
                {copySuccess ? content.copied : content.copyButton}
              </Button>
            </Box>
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="outlined"
              onClick={handleCloseShareDialog}
              sx={{ minWidth: 100 }}
            >
              {content.closeButton}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};
